import { format as formatDate } from 'date-fns';
import { derived, get, writable } from 'svelte/store';

import { BizCardsRoutingService } from '@client/services/bizcards-routing';
import { GroupsService } from '@client/services/groups';

import { GROUP_DEFAULTS } from '@client/enums/group-config';

function arrayStore() {
  const { subscribe, set, update } = writable([]);
  return {
    subscribe,
    set: (v) => set(Array.isArray(v) ? v : []),
    update: (fn) =>
      update((curr) => {
        const next = fn(curr);
        return Array.isArray(next) ? next : [];
      })
  };
}

export const selectedGroups = arrayStore();
export const checked = writable(new Set());
export const userGroupMap = writable(new Map());
export const applyMode = writable('modify');
export const targetGroupForAdditions = writable(null);

export function reset() {
  selectedGroups.set([]);
  checked.set(new Set());
  userGroupMap.set(new Map());
  applyMode.set('modify');
  targetGroupForAdditions.set(null);
}

export const groupEmails = derived(userGroupMap, ($map) => new Set($map.keys()));

export const additions = derived(
  [checked, groupEmails],
  ([$checked, $groupEmails]) => [...$checked].filter((email) => !$groupEmails.has(email))
);

export const removals = derived(
  [checked, userGroupMap],
  ([$checked, $userGroupMap]) => {
    const out = new Map();
    for (const [email, groups] of $userGroupMap) {
      if (!$checked.has(email)) {
        out.set(email, new Set(groups));
      }
    }
    return out;
  }
);

export const hasManualSelections = derived(
  [additions, removals],
  ([$additions, $removals]) => $additions.length > 0 || $removals.size > 0
);

export const applyState = derived(
  [selectedGroups, hasManualSelections],
  ([$selectedGroups, $hasManualSelections]) => {
    if ($selectedGroups.length === 0) return 'no-groups';
    if (!$hasManualSelections) return 'pure-groups';
    return 'mixed';
  }
);

export const pushDisabled = derived(
  [applyState, applyMode, additions, selectedGroups, targetGroupForAdditions],
  ([$applyState, $applyMode, $additions, $selectedGroups, $target]) => {
    if ($applyState !== 'mixed') return false;
    if ($applyMode !== 'modify') return false;
    if ($additions.length === 0) return false;
    if ($selectedGroups.length < 2) return false;
    return !$target;
  }
);

// Sender records use the username (local part of the email) as their key, matching
// how the existing users-selector (manage/groups) writes them. Comparing/storing
// full emails here silently drops removals on any group authored elsewhere.
const toUsername = (email) => email.split('@').shift();

export async function updateAssignmentAndUsers(blockname) {
  const mode = get(applyMode);
  const $applyState = get(applyState);
  const $checked = [...get(checked)];

  // Force 'create' when no groups are selected.
  const effectiveMode = $applyState === 'no-groups' ? 'create' : mode;

  let groupNames;

  if (effectiveMode === 'create') {
    const date = formatDate(new Date(), 'DDDHHmmss');
    const groupname = `${blockname}-users-${date}`;
    const newGroup = await GroupsService.createGroup({
      ...GROUP_DEFAULTS,
      groupname,
      senders: $checked.map((email) => ({ username: toUsername(email) }))
    });

    if (newGroup?.status >= 400) {
      return { success: false, failedStep: 'group-create' };
    }

    groupNames = [newGroup.groupname || groupname];
  } else {
    // applyMode === 'modify'
    const $additions = get(additions);
    const $selectedGroups = get(selectedGroups);
    const $target = get(targetGroupForAdditions);

    const allGroups = await GroupsService.getAll();
    const groupsByName = Object.fromEntries(allGroups.map((g) => [g.groupname, g]));

    // Determine the additions target upfront so removals on that group can be deferred.
    const additionsTargetName =
      $additions.length > 0
        ? $selectedGroups.length === 1
          ? $selectedGroups[0].value
          : $target
        : null;

    // Track in-memory mutations so additions can build on top of removals.
    const pendingSenders = new Map(); // groupName → updated senders array

    const $removals = get(removals);
    if ($removals.size > 0) {
      const removalsByGroup = new Map();
      for (const [email, groupSet] of $removals) {
        for (const groupName of groupSet) {
          if (!removalsByGroup.has(groupName)) removalsByGroup.set(groupName, new Set());
          removalsByGroup.get(groupName).add(email);
        }
      }
      for (const [groupName, emails] of removalsByGroup) {
        const group = groupsByName[groupName];
        const usernamesToRemove = new Set([...emails].map(toUsername));
        const newSenders = (group?.senders || []).filter((s) => !usernamesToRemove.has(s.username));
        pendingSenders.set(groupName, newSenders);
        // Defer the API call if additions will also update this group (combined call later).
        if (groupName === additionsTargetName) continue;
        const updateResult = await GroupsService.updateGroup({ ...group, senders: newSenders });
        if (updateResult?.status >= 400) {
          return { success: false, failedStep: 'group-update', failedGroup: groupName };
        }
      }
    }

    if ($additions.length > 0) {
      const targetName = additionsTargetName;
      const group = groupsByName[targetName];
      const currentSenders = pendingSenders.has(targetName)
        ? pendingSenders.get(targetName)
        : (group?.senders || []);
      const existingUsernames = new Set(currentSenders.map((s) => s.username));
      const newSenders = [
        ...currentSenders,
        ...$additions
          .map(toUsername)
          .filter((username) => !existingUsernames.has(username))
          .map((username) => ({ username }))
      ];
      const updateResult = await GroupsService.updateGroup({ ...group, senders: newSenders });
      if (updateResult?.status >= 400) {
        return { success: false, failedStep: 'group-update', failedGroup: targetName };
      }
    }

    groupNames = $selectedGroups.map((g) => g.value);
  }

  const allGroups = await GroupsService.getAll();
  const sendergroups = allGroups.filter(({ groupname }) => groupNames.includes(groupname));

  const route = await BizCardsRoutingService.updateRoute({
    blockname,
    sendergroups,
    status: true,
    priority: 0,
    tags: []
  });

  if (route?.status >= 400) {
    return { success: false, failedStep: 'route-create' };
  }

  // Reorder: new route to highest priority.
  const existing = await BizCardsRoutingService.getAll();
  const ids = existing.map(({ _id }) => _id);
  if (route._id && ids.length > 1) {
    const reordered = [route._id, ...ids.filter((id) => id !== route._id)];
    await BizCardsRoutingService.saveRouteOrder(reordered);
  }

  return { success: true, route };
}
